/**
 * Cipher & Row pricing · verification-only launch (May 2026).
 *
 * Nine tiers across three roles. Tier IDs are preserved for existing
 * customers (free, pro, broker, enterprise); new tiers add namespaced
 * IDs (verified-plus, solo, broker-free, broker-pro, broker-unlimited).
 *
 * The asymmetric model (locked 2026-05-12 with Louis):
 *   Carriers and dispatchers look up brokers UNLIMITED.
 *   Brokers pay to look up carriers — that's the gated, fast-hitting tier.
 *   Free distribution on the carrier side, paid value on the broker side.
 *
 * Repriced 2x (rounded to 9-endings) 2026-07-27, Jack + Louis phone ruling:
 * the market's price floor for broker vetting runs far above this ladder even
 * at 2x, so every tier still undercuts the incumbent for the same size band.
 * Competitor prices stay internal — never cite one publicly.
 *
 * Repriced again 1.5x on 2026-08-02 (founder ruling), same 9-ending rounding,
 * rounding UP: 59→89, 99→149, 199→299, 499→749, 299→449, 799→1,199, 1,999→2,999.
 *
 * ⚠ TWO NUMBERS COLLIDED IN THIS REPRICE. Dispatcher Pro is now $299 — the
 * price Broker Essential used to carry — and Broker Essential moved to $449. A
 * find/replace on "$299" across marketing copy will therefore corrupt one of
 * them. Change price strings by TIER, never by value.
 *
 * ⚠ THE PROCESSOR CATALOG IS THE THING THAT ACTUALLY CHARGES. These numbers are
 * display only; checkout resolves a Paddle/Stripe price id from `tierKey`. Until
 * the catalog is repriced, the page says $449 and the card is charged $299. See
 * the 2026-07-27 reprice runbook in vernot — and note a migration CANNOT
 * reprice the credit pack once the Paddle catalog exists.
 *
 * Carrier:
 *   Free            $0/mo always       Unlimited broker lookups · registry profile
 *   Verified+       $89/mo             Active monitoring + 60 scan credits/mo
 *                                      (NOT a badge, NOT analytics — this header
 *                                      claimed both while the tier below listed
 *                                      neither, and the homepage copied it. The
 *                                      badge feature `cr_verified` is in
 *                                      UNRELEASED_FEATURES and `embed_badge` is
 *                                      flagged off. Keep this header in sync with
 *                                      the CARRIER tier definition below.)
 *
 * TRIAL (founder ruling 2026-08-02, replaces the perpetual free tiers):
 *   Every paid plan starts with a 14-day free trial, no card. During the
 *   trial EVERY plan is ceilinged at 100 credits (so a Broker Scale trial is
 *   not 7,500 free credits), and sealed-record artifacts — audit-trail PDFs,
 *   record packs, the vault CSV — cannot be DOWNLOADED. Records still seal and
 *   still accrue; they become downloadable on conversion ("own the record once
 *   you pay"). When the window closes, dispatchers and brokers have NO free
 *   allowance. Carrier Free is untouched and stays $0 forever.
 *   Enforced by public.check_usage_limit + the checkSealedRecordDownload gate
 *   in vernot; mirrored for display in src/lib/trial.ts. Change all three
 *   together — the mirrors can only under-report, never grant.
 *
 * Dispatcher:
 *   Solo            $149/mo            500 credits/mo · unlimited
 *                                      broker lookups · 1 seat
 *   Pro             $299/mo (popular)  1,000 credits/mo · unlimited
 *                                      broker lookups · 3 seats
 *   Scale           $749/mo            2,500 credits/mo · API + MCP · 10 seats
 *
 * Broker (Louis's ladder, 2026-05-12, undercuts Highway at every step):
 *   (No perpetual free tier — retired 2026-08-02, see TRIAL section below.)
 *   Essential       $449/mo            250 credits/mo · 3 seats
 *                                      BMC-84 bond live + double-broker
 *                                      detection + reputation analytics.
 *                                      Built for brokers operating under MC authority.
 *                                      (Renamed from "Standard" 2026-05-12 per
 *                                      /hivesim PT3 finding: legal-posture
 *                                      framing prevents cannibalization by
 *                                      Dispatcher Pro.)
 *   Pro             $1,199/mo (popular) 1,500 credits/mo · hourly
 *                                      monitoring · API · 10 seats
 *   Scale           $2,999/mo          7,500 credits/mo · real-time
 *                                      monitoring · full API · 25 seats
 *   Enterprise      Custom             Sales-led · SLA · Carrier411
 *                                      displacement. No published floor
 *                                      (founder ruling 2026-07-23: incumbent
 *                                      enterprise pricing runs far above the
 *                                      old $2,500 figure, so a printed floor
 *                                      capped every negotiation; quote
 *                                      deal-by-deal instead).
 *
 * Annual billing is 20% off monthly. Fair-use overage rate is INTERNAL
 * (post-login surfaces only, never the public pricing page) — see OVERAGE_RATE.
 * Caps warn by email at 80/100/150%. When a paid tier's monthly credits are
 * exhausted, credit-metered actions BLOCK until a credit pack covers them or
 * the plan is upgraded (packs are the one overage mechanism; founder ruling
 * 2026-07-12, enforced in vernot _shared/usage.ts checkAndIncrement). Hard
 * abuse blocks (sybil/swarm) are separate.
 */

import { TRIAL_CREDIT_CAP, TRIAL_DAYS } from "@/lib/trial";

export type Role = "carrier" | "dispatcher" | "broker";

export interface UsageLimits {
  /** Soft fair-use cap on credits/mo. -1 = no cap. */
  verifications: number;
  /** Team seats included. -1 = unlimited. */
  seats: number;
}

export interface PricingTier {
  id: string;
  role: Role;
  name: string;
  subtitle: string;
  /** Human-readable usage summary for marketing surfaces. */
  usage: string;
  seats: string;
  /** Monthly price in USD. 0 = free, null = custom/contact sales. */
  monthly: number | null;
  /** Annual price per month in USD (20% off monthly). null when monthly is null. */
  annual: number | null;
  /** Founding cohort price. 0 = free for life, undefined = no founding offer. */
  foundingMonthly?: number | null;
  /** Founding cohort eligibility + lock duration text shown in UI. */
  foundingNote?: string;
  /**
   * Short positioning hint shown beneath the price. Used to clarify when a tier
   * is the right choice relative to neighbouring tiers (e.g. Essential vs Pro).
   */
  positioningNote?: string;
  limits: UsageLimits;
  popular?: boolean;
  /**
   * Marks the guided default for a role that does not set `popular`
   * (e.g. the carrier and dispatcher recommended pick). Renders the same
   * "Most Popular" badge + card emphasis as `popular` (2026-08-03: the pricing
   * page dropped the separate "Recommended" label). Never set both on tiers in
   * the same role section — the badge would appear twice in one grid.
   */
  recommended?: boolean;
  /** When true, tier is not yet purchasable. Renders a "Coming soon" badge and waitlist CTA instead of checkout. */
  comingSoon?: boolean;
  /**
   * When true, the tier is the default/starting state a company lands on (e.g.
   * a dispatcher free trial) and is NOT shown as a purchasable card on the
   * public pricing page or billing upgrade list. The dashboard still resolves
   * to it for usage display.
   */
  hidden?: boolean;
  /**
   * Tier id whose plan this tier includes ("Everything in X, plus"). Renderers
   * show a styled inheritance line resolved from this field, so `features`
   * must list only the DELTA over the inherited tier — never repeat bullets
   * the inherited tier already has.
   */
  inheritsFrom?: string;
  features: string[];
  cta: string;
  /**
   * Stable tier key sent to create-checkout-session. The edge function
   * resolves this to the actual Stripe price ID via
   * Deno.env.get(`STRIPE_PRICE_${tierKey.toUpperCase()}`). Undefined for free
   * tiers (no checkout). Allowed values: verified_plus, solo, pro, broker,
   * broker_pro, broker_unlimited, enterprise.
   */
  tierKey?: string;
}

/** 20% off monthly when billed annually. */
export const ANNUAL_DISCOUNT = 0.20;

/**
 * Fair-use overage rate: $2.25 per credit beyond the soft cap.
 * Founder ruling 2026-07-12 (v2, after hivesim wf_9d13f347-7cd): the pack
 * per-credit rate must be STRICTLY ABOVE every plan's bundled rate so packs
 * can never substitute for a subscription. Binding constraint is Broker
 * Essential, now $449/250cr = $1.796/cr; $2.25 clears it with margin.
 *
 * This rate HAD to move with the 1.5x reprice (2026-08-02). Left at $1.50 it
 * would have fallen BELOW Broker Essential's new bundled rate, inverting the
 * ruling: buying packs would have been cheaper per credit than the plan that
 * bundles them. pricing-tiers.test.ts now pins the invariant so the next
 * reprice cannot break it silently.
 * UNPUBLISHED: this rate renders on post-login surfaces only (FleetTierModal),
 * never the public pricing page — a public per-lookup price is a free
 * comparison ad for flat-rate rivals (premortem finding; Highway stays
 * quote-only). Matches the credit-pack rate below; server-side credit_packs
 * row is authoritative (repriced via Paddle catalog + service-role SQL, see
 * the 2026-07-27 reprice runbook in vernot — a migration CANNOT reprice it
 * once the Paddle catalog exists).
 */
export const OVERAGE_RATE = 2.25;

/**
 * Credit pack (prepaid top-up credits) — display metadata only. The SERVER is
 * authoritative on price and credits (create-paddle-checkout resolves the pack
 * from the credit_packs table); this is just what the Buy Credits card renders.
 *
 * ONE SKU by design: 100 credits at $225 ($2.25/credit, matching OVERAGE_RATE),
 * bought in quantities of 1-10. Pack credits never expire but are spendable only
 * while an active paid subscription exists, and are consumed AFTER the monthly
 * plan allowance. Gated behind the `credit_packs` PhaseFeature + backend flags.
 */
export const CREDIT_PACK = {
  key: "pack_100",
  credits: 100,
  priceUsd: 225,
  minQuantity: 1,
  maxQuantity: 10,
} as const;

function annualOf(monthly: number): number {
  return Math.round(monthly * (1 - ANNUAL_DISCOUNT));
}

export const PRICING_TIERS: PricingTier[] = [
  // CARRIER
  {
    id: "free",
    role: "carrier",
    name: "Free",
    subtitle: "Vet any broker. Free.",
    usage: "Unlimited broker lookups · listed in CR registry",
    seats: "1 seat",
    monthly: 0,
    annual: 0,
    limits: { verifications: -1, seats: 1 },
    features: [
      "Unlimited broker lookups",
      "FMCSA authority, bond status, insurance, double-broker risk",
      "Canadian flags (NTBA, ODBus, CVOR, NSC, CBSA)",
      "Days-to-pay and scam-flag history",
      "Listed in CR carrier registry (broker-searchable)",
      "Independent third-party timestamps on sealed records, the same standard used for signed contracts and patent filings (RFC 3161)",
      "1 seat",
    ],
    cta: "Look up a broker, free",
  },
  {
    id: "verified-plus",
    role: "carrier",
    name: "Verified+",
    subtitle: "Get found. Stay verified.",
    usage: "Active monitoring · 60 scan credits/mo",
    seats: "1 seat",
    monthly: 89,
    annual: annualOf(89),
    recommended: true,
    limits: { verifications: -1, seats: 1 },
    inheritsFrom: "free",
    features: [
      "Real-time monitoring alerts (insurance, authority, bond)",
      "Daily monitoring of insurance, authority, and bond status",
      "Scan broker paperwork for fraud (60 credits/mo, a typical packet costs 3 credits)",
    ],
    cta: "Start Verified+",
    tierKey: "verified_plus",
  },
  // DISPATCHER
  {
    // The pre-plan / trial holding state for a dispatcher, NOT a purchasable
    // tier and NOT a perpetual free allowance any more.
    //
    // Before 2026-08-02 this was a standing "25 credits/week, forever" tier.
    // The founder killed it: it gave the product away indefinitely to anyone
    // who never converted. A dispatcher now picks a paid plan, gets 14 days
    // free capped at 100 credits, and at the end of that window pays or stops.
    //
    // The id and the usage_limits row both survive because check_usage_limit
    // still resolves free+dispatcher to this bucket — its monthly_credits is
    // now 0, which is what makes the paywall bite for an account that never
    // started a trial. `hidden` keeps it off every purchasable list.
    id: "dispatcher-trial",
    role: "dispatcher",
    name: "Free trial",
    subtitle: "14 days free on any plan",
    usage: `${TRIAL_DAYS}-day trial · up to ${TRIAL_CREDIT_CAP} credits · 1 seat`,
    seats: "1 seat",
    monthly: 0,
    annual: 0,
    hidden: true,
    // 0 = no standing allowance. Entitlement during the window comes from the
    // CHOSEN plan, ceilinged at TRIAL_CREDIT_CAP by check_usage_limit.
    limits: { verifications: 0, seats: 1 },
    features: [
      `${TRIAL_DAYS} days free on any plan, no card required`,
      `Up to ${TRIAL_CREDIT_CAP} credits during the trial`,
      "Carrier and broker lookups (1 credit each)",
      "Identity and re-brokering risk signals on every lookup",
      "Canadian verification (CVOR, CTQ, NSC)",
      // Deliberately NOT claiming downloadable sealed records: audit-trail
      // PDFs, record packs and the vault CSV are paid-only. Records still
      // seal during the trial and download the moment the account converts.
      "Records seal to your Compliance Vault from day one",
      "1 seat",
    ],
    cta: "Start your 14-day free trial",
  },
  {
    id: "solo",
    role: "dispatcher",
    name: "Solo",
    subtitle: "1 to 5 truck operators",
    usage: "500 credits/mo · 1 seat",
    seats: "1 seat",
    monthly: 149,
    annual: annualOf(149),
    positioningNote: "A single prevented bad load can outweigh a year on this plan.",
    limits: { verifications: 500, seats: 1 },
    features: [
      "500 credits per month",
      "Carrier and broker lookups (1 credit each)",
      "Monitoring on saved carriers and brokers",
      "Identity and re-brokering risk signals on every lookup",
      "Trust scores (6 tiers)",
      "Canadian verification (CVOR, CTQ, NSC)",
      "Packet scanner for carrier-packet verification",
      "Compliance Vault with sealed, verifiable scan records",
      "Independent third-party timestamps on sealed records, the same standard used for signed contracts and patent filings (RFC 3161)",
      "1 user seat",
    ],
    cta: "Start Solo",
    tierKey: "solo",
  },
  {
    id: "pro",
    role: "dispatcher",
    name: "Pro",
    subtitle: "5 to 30 truck dispatch",
    usage: "1,000 credits/mo · 3 seats",
    seats: "3 seats",
    monthly: 299,
    annual: annualOf(299),
    recommended: true,
    positioningNote: "A single prevented bad load can outweigh a year on this plan.",
    limits: { verifications: 1000, seats: 3 },
    inheritsFrom: "solo",
    features: [
      "1,000 credits per month",
      "Full monitoring with real-time alerts",
      "Bulk CSV verify",
      "Compliance audit trail",
      "Packet scanner with document fraud cross-checks",
      "3 user seats",
    ],
    cta: "Start Pro",
    tierKey: "pro",
  },
  {
    id: "dispatcher-scale",
    role: "dispatcher",
    name: "Scale",
    subtitle: "Multi-desk dispatch offices",
    usage: "2,500 credits/mo · 10 seats",
    seats: "10 seats",
    monthly: 749,
    annual: annualOf(749),
    positioningNote: "Stop sharing logins. Grow without the sales call.",
    limits: { verifications: 2500, seats: 10 },
    inheritsFrom: "pro",
    features: [
      "2,500 credits per month",
      "10 user seats",
      "API + MCP access",
      "Priority support",
    ],
    cta: "Start Scale",
    tierKey: "dispatcher_scale",
  },
  {
    id: "dispatcher-enterprise",
    role: "dispatcher",
    name: "Enterprise",
    subtitle: "Large fleets & multi-office dispatch",
    usage: "Custom credit volume · custom seats",
    seats: "Custom",
    monthly: null,
    annual: null,
    limits: { verifications: -1, seats: -1 },
    inheritsFrom: "dispatcher-scale",
    features: [
      "Custom credit volume",
      "Volume-based pricing",
      "Priority support with SLA",
      "Enterprise-grade third-party timestamp authority",
      "Dedicated account manager",
      "Custom seats + guided onboarding",
    ],
    cta: "Talk to sales",
  },
  // BROKER
  {
    // Pre-plan / trial holding state for a broker. See the dispatcher-trial
    // note above: the standing "25 credits/week forever" tier was retired
    // 2026-08-02 in favour of one 14-day trial. Kept (hidden) because
    // check_usage_limit resolves free+broker to this bucket and the Essential
    // tier inherits from it; its usage_limits row is now 0 credits.
    id: "broker-free",
    role: "broker",
    name: "Free trial",
    subtitle: "14 days free on any plan",
    usage: `${TRIAL_DAYS}-day trial · up to ${TRIAL_CREDIT_CAP} credits · 1 seat`,
    seats: "1 seat",
    monthly: 0,
    annual: 0,
    hidden: true,
    limits: { verifications: 0, seats: 1 },
    features: [
      `${TRIAL_DAYS} days free on any plan, no card required`,
      `Up to ${TRIAL_CREDIT_CAP} credits during the trial`,
      "FMCSA + Canadian carrier lookup",
      "Authority and insurance status",
      // "Respond to reviews" is appended by the pricing page behind
      // BROKER_REVIEWS_ENABLED — the reviews feature hasn't shipped yet.
      "Claim your broker profile",
      "Records seal to your Compliance Vault from day one",
      "1 seat",
    ],
    cta: "Start your 14-day free trial",
  },
  {
    id: "broker",
    role: "broker",
    name: "Essential",
    // NOT "Required if you hold MC broker authority" — no regulation requires a
    // broker to buy a SaaS plan, and stating one to brokers (or to the counsel
    // who advises them) is a false statement of law. Keeps the legal-posture
    // positioning without asserting a duty that does not exist.
    subtitle: "Built for brokers operating under MC authority",
    usage: "250 credits/mo · 3 seats",
    seats: "3 seats",
    monthly: 449,
    annual: annualOf(449),
    positioningNote:
      "Best if you don't need higher credit volume.",
    limits: { verifications: 250, seats: 3 },
    inheritsFrom: "broker-free",
    features: [
      "250 credits per month",
      "BMC-84 bond live status (the legal posture core)",
      "Identity and re-brokering risk signals on every lookup",
      "Carrier monitoring with alerts",
      "Packet scanner for carrier verification",
      "Compliance Vault with sealed, verifiable scan records",
      "Full trust scores and fraud signals",
      "3 seats",
    ],
    cta: "Start Essential",
    tierKey: "broker",
  },
  {
    id: "broker-pro",
    role: "broker",
    name: "Pro",
    subtitle: "Growing brokerages, 50 to 500 loads/mo",
    usage: "1,500 credits/mo · 10 seats",
    seats: "10 seats",
    monthly: 1199,
    annual: annualOf(1199),
    popular: true,
    positioningNote: "A single prevented bad load can outweigh a year on this plan.",
    limits: { verifications: 1500, seats: 10 },
    inheritsFrom: "broker",
    features: [
      "1,500 credits per month",
      "Carrier monitoring with real-time alerts",
      "Packet scanner with document fraud cross-checks",
      "10 seats",
    ],
    cta: "Start Pro",
    tierKey: "broker_pro",
  },
  {
    id: "broker-unlimited",
    role: "broker",
    name: "Scale",
    subtitle: "High-volume brokerages, 500 to 2,000 loads/mo",
    usage: "7,500 credits/mo · 25 seats",
    seats: "25 seats",
    monthly: 2999,
    annual: annualOf(2999),
    positioningNote: "A single prevented bad load can outweigh a year on this plan.",
    limits: { verifications: 7500, seats: 25 },
    inheritsFrom: "broker-pro",
    features: [
      "7,500 credits per month",
      "Dedicated customer success manager",
      "25 seats",
    ],
    cta: "Start Scale",
    tierKey: "broker_unlimited",
  },
  {
    id: "enterprise",
    role: "broker",
    name: "Enterprise",
    subtitle: "Top-100 brokers, 2,000+ loads",
    usage: "Custom · sales-led",
    seats: "Custom",
    monthly: null,
    annual: null,
    limits: { verifications: -1, seats: -1 },
    inheritsFrom: "broker-unlimited",
    features: [
      "Custom credit volume",
      "Custom compliance rules and SLA",
      "Enterprise-grade third-party timestamp authority",
      "24/7 support team",
      // MCP itself is NOT Enterprise-only — every paid broker tier can connect
      // an AI client today. Only the elevated rate limits are Enterprise.
      "Priority MCP rate limits for AI-agent access",
    ],
    cta: "Talk to sales",
    tierKey: "enterprise",
  },
];

/**
 * Carrier-side tiers (Free + Verified+).
 *
 * Carrier "Free" is deliberately NOT swept up in the 2026-08-02 free-tier
 * retirement: it is $0 forever by design (unlimited broker lookups + a registry
 * profile) and is the distribution engine that fills the registry brokers pay
 * to search. Only the dispatcher and broker standing allowances were killed.
 */
export const CARRIER_TIERS = PRICING_TIERS.filter(t => t.role === "carrier");

/** Dispatcher tiers (Solo + Pro). Excludes the hidden free-trial state. */
export const DISPATCHER_TIERS = PRICING_TIERS.filter(t => t.role === "dispatcher" && !t.hidden);

/**
 * Public pricing-page dispatcher list.
 *
 * This used to append the free-trial state as a dispatcher "$0" card, for
 * parity with the carrier and broker free cards. Both of those reasons are
 * gone: the broker free card was retired 2026-08-02 and the dispatcher one was
 * never a plan, just a standing 25-credits/week allowance. Advertising a "$0"
 * dispatcher card now would promise an allowance the backend sets to 0.
 *
 * Identical to DISPATCHER_TIERS today. Kept as a distinct export so the public
 * page and the in-dash upgrade list can diverge again without a rename sweep.
 */
export const DISPATCHER_PUBLIC_TIERS = PRICING_TIERS.filter(
  t => t.role === "dispatcher" && !t.hidden,
);

/**
 * Broker tiers (Essential + Pro + Scale + Enterprise). Excludes the hidden
 * broker-free holding state — since 2026-08-02 there is no perpetual free
 * broker tier to advertise, only the 14-day trial that every paid plan starts
 * with.
 */
export const BROKER_TIERS = PRICING_TIERS.filter(t => t.role === "broker" && !t.hidden);

export function getTierById(id: string): PricingTier | undefined {
  return PRICING_TIERS.find(t => t.id === id);
}

/**
 * Feature list with the "Everything in X, plus:" inheritance line prepended,
 * for renderers that show plain string lists (dashboard billing cards). The
 * public pricing page renders the inheritance line itself (translated) from
 * `inheritsFrom` instead.
 */
export function featuresWithInheritance(tier: PricingTier): string[] {
  const base = tier.inheritsFrom ? getTierById(tier.inheritsFrom) : undefined;
  return base ? [`Everything in ${base.name}, plus:`, ...tier.features] : tier.features;
}

export function getTiersByRole(role: Role): PricingTier[] {
  return PRICING_TIERS.filter(t => t.role === role);
}

/**
 * Legacy tier names that may live in companies.subscription_tier from before
 * today's ids (e.g. dispatcher pricing was renamed from "starter"/"growth"/
 * "fleet"/"founding" to "solo"/"pro"). Safe to apply unconditionally: none of
 * these strings collide with a real id in any role's own tier list.
 */
const LEGACY_TIER_ALIASES: Record<string, string> = {
  starter: "solo",
  growth: "pro",
  fleet: "pro",
  founding: "pro",
};

/**
 * Role-aware tier resolver for a company's stored subscription_tier. Some ids
 * are NOT unique across roles — broker's top tier is literally "enterprise"
 * while dispatcher's is "dispatcher-enterprise" (both custom/sales-led).
 * A role-blind lookup (getTierById) resolves the bare string "enterprise" to
 * broker's tier even for a dispatcher company, which is wrong whenever a
 * dispatcher's subscription_tier was written as the generic "enterprise"
 * (e.g. a manually-provisioned/sales-led account). Resolve within the
 * company's own role first so that never happens.
 */
export function getTierForCompany(
  subscriptionTier: string | null | undefined,
  role: Role,
): PricingTier | undefined {
  if (!subscriptionTier) return undefined;
  const aliased = LEGACY_TIER_ALIASES[subscriptionTier] ?? subscriptionTier;

  const roleTiers = getTiersByRole(role);
  const withinRole = roleTiers.find(t => t.id === aliased);
  if (withinRole) return withinRole;

  // Generic "enterprise" with no exact id match in this role's own tiers:
  // fall back to this role's own sales-led/custom tier (monthly === null),
  // if it has one, rather than leaking another role's tier object.
  if (aliased === "enterprise") {
    const roleCustomTier = roleTiers.find(t => t.monthly === null);
    if (roleCustomTier) return roleCustomTier;
  }

  // Back-compat for ids that are already collision-free across roles (free,
  // broker-free, dispatcher-trial, solo, pro, verified-plus, broker,
  // broker-pro, broker-unlimited).
  return getTierById(aliased);
}

/**
 * Resolve the effective `usage_limits` tier the SAME way the backend
 * public.check_usage_limit does, so the client meter matches enforcement:
 *   - a free / unsubscribed / legacy-empty company is capped by its ROLE entry
 *     tier (carrier/trucker -> "free", broker -> "broker-free", else
 *     "dispatcher-trial"), NOT the bare "free" row (the carrier-unlimited tier)
 *   - "scale" maps to "broker-unlimited"
 *   - every other tier (including the paid "starter" = 50 credits) keeps its own
 *     usage_limits row
 * Shared by the dashboard usage strip (usePlanUsage) and the Billing usage card
 * so the credits limit is never re-derived two different ways. Keep in sync with
 * public.check_usage_limit in vernot.
 */
export function effectiveUsageTier(
  tier: string | null | undefined,
  role: string | null | undefined,
): string {
  const t = (tier ?? "").trim().toLowerCase() || "free";
  if (t === "free") {
    const r = (role ?? "dispatcher").toLowerCase();
    if (r === "carrier" || r === "trucker") return "free";
    if (r === "broker") return "broker-free";
    return "dispatcher-trial";
  }
  if (t === "scale") return "broker-unlimited";
  return t;
}

/** Within a role, return the next-up tier. Returns undefined if at top. */
export function getNextTier(currentId: string): PricingTier | undefined {
  const current = getTierById(currentId);
  if (!current) return undefined;
  const sameRole = getTiersByRole(current.role);
  const idx = sameRole.findIndex(t => t.id === currentId);
  if (idx >= 0 && idx < sameRole.length - 1) return sameRole[idx + 1];
  return undefined;
}

/**
 * Within a role, return the next tier forward that actually adds seats. Walks
 * the same-role ladder forward from the current tier and returns the first
 * tier whose seat allowance is strictly greater than the current tier's
 * (unlimited, -1, counts as greater). Returns undefined if no later tier adds
 * seats. Unlike getNextTier, this never promises a same-seat upgrade for a
 * seat upsell.
 */
export function getNextSeatTier(currentId: string): PricingTier | undefined {
  const current = getTierById(currentId);
  if (!current) return undefined;
  const currentSeats = current.limits.seats;
  // Unlimited current seats means nothing can add more.
  if (currentSeats === -1) return undefined;
  const sameRole = getTiersByRole(current.role);
  const idx = sameRole.findIndex(t => t.id === currentId);
  if (idx < 0) return undefined;
  for (let i = idx + 1; i < sameRole.length; i++) {
    const seats = sameRole[i].limits.seats;
    if (seats === -1 || seats > currentSeats) return sameRole[i];
  }
  return undefined;
}

export function getUsagePercent(used: number, limit: number): number {
  if (limit === -1 || limit === Infinity) return 0;
  if (limit <= 0) return 0;
  return Math.min(100, Math.round((used / limit) * 100));
}

/** Resolve a tier by its stable tierKey (inverse of tier.tierKey). */
export function getTierByKey(tierKey: string): PricingTier | undefined {
  return PRICING_TIERS.find(t => t.tierKey === tierKey);
}

/**
 * tier_keys a role may self-grant on the no-card trial. Mirrors the server-side
 * ROLE_ALLOWED_TIER_KEYS in vernot set-plan/logic.ts. Enterprise is sales-led
 * (never self-serve); free tiers need no trial.
 */
export const TRIAL_TIER_KEYS_BY_ROLE: Record<Role, string[]> = {
  carrier: ["verified_plus"],
  dispatcher: ["solo", "pro", "dispatcher_scale"],
  broker: ["broker", "broker_pro", "broker_unlimited"],
};

/** True when `role` is allowed to start a trial of `tierKey` (client mirror of set-plan). */
export function isTrialTierKeyAllowedForRole(
  role: Role | string | null | undefined,
  tierKey: string,
): boolean {
  if (!role) return false;
  const allowed = TRIAL_TIER_KEYS_BY_ROLE[role as Role];
  return !!allowed && allowed.includes(tierKey);
}
